import React, { useEffect, useId, useRef, useState } from 'react';
import TooltipPortal from './TooltipPortal';
import { useI18n } from '../../context/I18nContext';

const CatalogLinkIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1" />
  </svg>
);

const CatalogAssignmentIndicator = ({ code, description, status = 'resolved', className = '' }) => {
  const { t } = useI18n();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [position, setPosition] = useState({ left: 16, y: 0, placeBelow: false });
  const buttonRef = useRef(null);
  const tooltipId = useId();
  const isResolved = status === 'resolved';
  const showPopover = isHovered || isFocused || isPinned;

  const updatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const popoverWidth = Math.min(320, window.innerWidth - 32);
    const preferredLeft = rect.left - 8;
    setPosition({
      left: Math.min(Math.max(preferredLeft, 16), window.innerWidth - popoverWidth - 16),
      y: rect.top,
      placeBelow: rect.top < 120
    });
  };

  useEffect(() => {
    if (!code) return undefined;
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [code]);

  useEffect(() => {
    if (!isPinned) return undefined;

    const closeOnOutsideInteraction = (event) => {
      if (!buttonRef.current?.contains(event.target)) {
        setIsPinned(false);
        setIsFocused(false);
        setIsHovered(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setIsPinned(false);
        setIsFocused(false);
        setIsHovered(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideInteraction);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideInteraction);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isPinned]);

  if (!code) return null;

  const statusLabel = t(`cableForm.catalogStatus.${status}`);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`absolute -right-1.5 -top-1.5 z-10 inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 border-white shadow-sm transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 ${isResolved ? 'bg-indigo-600 text-white focus:ring-indigo-500' : 'bg-amber-500 text-white focus:ring-amber-500'} ${className}`}
        aria-label={t('cableForm.catalogDetailsLabel', { code })}
        aria-describedby={showPopover ? tooltipId : undefined}
        aria-expanded={showPopover}
        onClick={(event) => {
          event.stopPropagation();
          updatePosition();
          setIsPinned(previous => {
            if (previous) setIsFocused(false);
            return !previous;
          });
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseEnter={() => {
          updatePosition();
          setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => {
          updatePosition();
          setIsFocused(true);
        }}
        onBlur={() => setIsFocused(false)}
      >
        {isResolved ? <CatalogLinkIcon className="h-3 w-3" /> : <span className="text-[11px] font-black leading-none">!</span>}
      </button>

      <TooltipPortal show={showPopover}>
        <div
          id={tooltipId}
          role="tooltip"
          className="fixed z-[9999] w-max max-w-[min(20rem,calc(100vw-2rem))] rounded-lg bg-gray-900 px-3 py-2 text-left text-xs text-white shadow-lg pointer-events-none"
          style={position.placeBelow ? {
            left: `${position.left}px`,
            top: `${position.y + 26}px`,
          } : {
            left: `${position.left}px`,
            top: `${position.y - 8}px`,
            transform: 'translateY(-100%)'
          }}
        >
          <div className={`mb-1 font-semibold ${isResolved ? 'text-indigo-200' : 'text-amber-300'}`}>
            {t('cableForm.catalogDetailsTitle')}
          </div>
          <div className="break-all font-mono font-semibold text-white">{code}</div>
          {description && <div className="mt-1 max-w-xs text-gray-200">{description}</div>}
          {!isResolved && <div className="mt-1 font-medium text-amber-300">{statusLabel}</div>}
        </div>
      </TooltipPortal>
    </>
  );
};

export default CatalogAssignmentIndicator;
